import { isNumericLiteral, isFlowExpressionNumericField, flowNumericKind, schemaVariableTypeHint } from '../../../schemaform/variableBinding.js';

/**
 * Turn a numeric string into a number for a literal-only integer/number field.
 * Expressions and other text are left unchanged. Blank strings become undefined
 * so schema validation treats the field as unset.
 * @param {unknown} value
 * @param {boolean} integerOnly
 * @returns {unknown}
 */
export function coerceLiteralNumericValue(value, integerOnly) {
    if (typeof value === 'number')
        return value;
    if (typeof value !== 'string')
        return value;
    if (value.trim() === '')
        return undefined;
    if (!isNumericLiteral(value, integerOnly))
        return value;
    const n = Number(value.trim());
    if (!Number.isFinite(n))
        return value;
    if (integerOnly && !Number.isInteger(n))
        return value;
    return n;
}

/**
 * Value to persist from an expression-capable numeric control.
 * Literals become numbers. Expressions stay strings. Blank stays blank.
 * @param {unknown} value
 * @param {boolean} integerOnly
 * @returns {unknown}
 */
export function storedExpressionNumericValue(value, integerOnly) {
    if (value == null || value === '')
        return '';
    if (typeof value === 'number') {
        if (!Number.isFinite(value))
            return value;
        if (integerOnly && !Number.isInteger(value))
            return String(value);
        return value;
    }
    if (typeof value === 'string' && isNumericLiteral(value, integerOnly)) {
        const n = Number(value.trim());
        if (Number.isFinite(n) && (!integerOnly || Number.isInteger(n)))
            return n;
    }
    return typeof value === 'string' ? value : String(value);
}

function expressionNumericIntegerOnly(prop) {
    return flowNumericKind(schemaVariableTypeHint(prop)) === 'integer';
}

/**
 * Coerce a literal string on an expression-numeric leaf. Expressions and blanks stay.
 * @param {object} prop
 * @param {unknown} value
 * @returns {unknown}
 */
function coerceExpressionNumericLeaf(prop, value) {
    if (typeof value === 'string' && value.trim() === '')
        return value;
    const next = coerceLiteralNumericValue(value, expressionNumericIntegerOnly(prop));
    return typeof next === 'number' ? next : value;
}

/**
 * Walk a resolved Flow editor schema and coerce literal numeric strings in place.
 * @param {object|null|undefined} schema
 * @param {object|null|undefined} model
 */
export function coerceModelToLiteralNumerics(schema, model) {
    if (!schema || model == null || typeof model !== 'object')
        return;
    if (Array.isArray(model)) {
        const itemSchema = schema.items;
        if (!itemSchema || typeof itemSchema !== 'object')
            return;
        for (let i = 0; i < model.length; i++) {
            if (itemSchema.type === 'integer' || itemSchema.type === 'number') {
                const next = coerceLiteralNumericValue(model[i], itemSchema.type === 'integer');
                if (next === undefined)
                    model[i] = undefined;
                else
                    model[i] = next;
            }
            else if (isFlowExpressionNumericField(itemSchema)) {
                model[i] = coerceExpressionNumericLeaf(itemSchema, model[i]);
            }
            else if (itemSchema.type === 'object' || itemSchema.type === 'array') {
                coerceModelToLiteralNumerics(itemSchema, model[i]);
            }
        }
        return;
    }
    const props = schema.properties;
    if (!props || typeof props !== 'object')
        return;
    for (const key of Object.keys(props)) {
        const prop = props[key];
        if (!prop || typeof prop !== 'object' || !Object.prototype.hasOwnProperty.call(model, key))
            continue;
        if (prop.type === 'integer' || prop.type === 'number') {
            const next = coerceLiteralNumericValue(model[key], prop.type === 'integer');
            if (next === undefined)
                delete model[key];
            else
                model[key] = next;
        }
        else if (isFlowExpressionNumericField(prop)) {
            model[key] = coerceExpressionNumericLeaf(prop, model[key]);
        }
        else if (prop.type === 'object' || prop.type === 'array') {
            coerceModelToLiteralNumerics(prop, model[key]);
        }
    }
}
